import { action, makeObservable, observable, reaction } from 'mobx'
import config from '~/config'
import { Media, Post } from '~/models'
import { NewsService } from '~/stores'
import { FormModel, isSuccessResult, SubmitResult } from '~/ui/form'

const storage: Storage | null = 'localStorage' in global ? (global as any).localStorage : null

function draftKey(post: Post | null) {
  return `news.post_draft.${post?.id ?? 'new'}`
}

function loadDraft(post: Post | null): string | null {
  try {
    return storage?.getItem(draftKey(post)) ?? null
  } catch {
    return null
  }
}

function saveDraft(post: Post | null, body: string) {
  try {
    if (body.trim() === '') {
      storage?.removeItem(draftKey(post))
    } else {
      storage?.setItem(draftKey(post), body)
    }
  } catch {
    // Ignore storage errors (e.g. private browsing, quota exceeded).
  }
}

function clearDraft(post: Post | null) {
  try {
    storage?.removeItem(draftKey(post))
  } catch {
    // Ignore storage errors.
  }
}

export default class PostFormModel implements FormModel {

  constructor(
    private readonly service: NewsService,
    private readonly post: Post | null,
  ) {
    this.feed = this.defaultFeed()

    const draft = loadDraft(this.post)
    if (draft != null) {
      this.body = draft
    }

    makeObservable(this)

    this.disposeDraftReaction = reaction(
      () => this.body,
      body => { saveDraft(this.post, body) },
    )
  }

  private readonly disposeDraftReaction: () => void

  public dispose() {
    this.disposeDraftReaction()
  }

  @observable
  public feed: string | null = null

  private defaultFeed() {
    if (this.post?.feed.id != null) { return this.post.feed.id }
    if (this.service.openFeeds.length === 1) { return this.service.openFeeds[0].id }
    return null
  }

  @observable
  public title: string = this.post?.title ?? ''

  // Restored from a locally saved draft if the user navigated away before finishing this post.
  @observable
  public body: string = this.post?.body ?? ''

  @observable
  public notify: boolean = false

  //------
  // Media

  public get mayAddMedia() {
    return this.media.length < config.news.maxMediaCount
  }

  @observable
  public media: Media[] = this.post?.media ?? []

  @action
  public addMedia(media: Media) {
    if (!this.mayAddMedia) { return }
    this.media = [...this.media, media]
  }

  @action
  public removeMedia(media: Media) {
    this.media = this.media.filter(it => it.id !== media.id)
  }

  //------
  // Submit

  public get maySubmit() {
    if (this.body.trim().length > 1) { return true }
    if (this.media.length > 0) { return true }
    if (this.feed != null) { return true }

    return false
  }

  public async submit(): Promise<SubmitResult | undefined> {
    if (this.feed == null) { return }

    const result = this.post == null
      ? await this.service.addPost(this.feed, {
        title: this.title.trim(),
        body:  this.body,
        media: this.media.map(it => it.id),
      }, this.notify)
      : await this.service.updatePost(this.post.id, {
        title: this.title.trim(),
        body:  this.body,
        media: this.media.map(it => it.id),
        feed:  this.feed,
      })

    if (isSuccessResult(result)) {
      clearDraft(this.post)

      // The form (and this model) stays mounted, so clear it for the next new post.
      if (this.post == null) {
        this.clear()
      }
    }

    return result
  }

  @action
  private clear() {
    this.feed   = this.defaultFeed()
    this.title  = ''
    this.body   = ''
    this.media  = []
    this.notify = false
  }

}